import React, { useState, useEffect } from "react";
import { db, storage } from "../firebase";
import { doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import "./AddContentModal.css"; // Reusing styles

function EditContentModal({ isOpen, onClose, media, onContentUpdated }) {
  const [name, setName] = useState("");
  const [mediaType, setMediaType] = useState("");
  const [duration, setDuration] = useState(10);
  const [newFile, setNewFile] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState("");

  // Reset state when modal opens
  useEffect(() => {
    if (media) {
      setName(media.name);
      setMediaType(media.mediaType);
      setDuration(media.duration || 10);
      setNewFile(null); // Reset file input
    }
  }, [media]);

  const handleFileChange = (e) => {
    setNewFile(e.target.files[0]);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!name) {
      setError("Name cannot be empty.");
      return;
    }

    setIsUpdating(true);
    try {
      const mediaRef = doc(db, "media", media.id);
      let updatedData = { name, mediaType, duration };

      // If a new file is selected, replace the old one
      if (newFile) {
        const fileRef = ref(storage, `media/${mediaType}/${Date.now()}-${newFile.name}`);
        
        // Upload the new file
        await uploadBytes(fileRef, newFile);
        const newFileUrl = await getDownloadURL(fileRef);
        updatedData.url = newFileUrl;

        // Delete the old file if it exists
        if (media.url) {
          try {
            const oldFileRef = ref(storage, media.url);
            await deleteObject(oldFileRef);
          } catch (err) {
            console.warn("Old file could not be deleted:", err.message);
          }
        }
      }

      // Update Firestore document
      await updateDoc(mediaRef, updatedData);

      // Notify parent component to refresh the content list
      if (onContentUpdated) {
        onContentUpdated();
      }

      onClose();
    } catch (err) {
      console.error("Error updating media:", err.message);
      setError("Failed to update content. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <h2>Edit Content</h2>
        <form onSubmit={handleUpdate}>
          {/* Media Type Selection */}
          <div className="media-type-selection">
            {["Image", "Video", "Document"].map((type) => (
              <button
                type="button"
                key={type}
                className={`media-type-button ${mediaType === type.toLowerCase() ? "selected" : ""}`}
                onClick={() => setMediaType(type.toLowerCase())}
              >
                {type}
              </button>
            ))}
          </div>

          {/* Name Input */}
          <div className="form-group">
            <label>Name:</label>
            <input
              type="text"
              value={name}
              placeholder="Enter file name"
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          {/* Upload New File (Optional) */}
          <div className="form-group">
            <label>Replace File:</label>
            <div className="file-upload">
              <input type="file" onChange={handleFileChange} />
              {newFile && <span className="file-name">{newFile.name}</span>}
            </div>
          </div>

          {/* Duration Input (Only for Image/Document) */}
          {(mediaType === "image" || mediaType === "document") && (
            <div className="form-group">
              <label>Default Duration (seconds):</label>
              <input
                type="number"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                min="1"
                required
              />
            </div>
          )}

          {/* Actions */}
          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn cancel">
              Cancel
            </button>
            <button type="submit" className="btn save" disabled={isUpdating}>
              {isUpdating ? "Updating..." : "Save"}
            </button>
          </div>
        </form>
        {error && <p className="error-message">{error}</p>}
      </div>
    </div>
  );
}

export default EditContentModal;
